// Calendar of one habit: streaks and rate on top, a month to browse and
// correct below (tap a past day to check it off or undo it).
import { ArrowLeft, CalendarCheck, Flame, Pencil, Play, TrendingUp, Trophy } from 'lucide-react';
import { useMemo, useState } from 'react';
import { startOfMonth, type DayKey } from '../../shared/days';
import { completionRate, scheduleLabel, streaks, weekCount } from '../../shared/schedule';
import { useEditor } from '../components/EditorContext';
import { EmojiTile } from '../components/EmojiTile';
import { MonthCalendar } from '../components/MonthCalendar';
import { useToast } from '../components/Toasts';
import { errorMessage } from '../lib/api';
import { percent, relativeDay } from '../lib/format';
import { tap } from '../lib/haptics';
import { goBack } from '../lib/router';
import { daysOf, setDone, updateHabit, useStore } from '../lib/store';
import { useToday } from '../lib/useToday';

export function HabitView({ id }: { id: string }) {
  const { habits, done, loaded } = useStore();
  const today = useToday();
  const editor = useEditor();
  const toast = useToast();
  const [month, setMonth] = useState<DayKey>(() => startOfMonth(today));
  const habit = habits.find((h) => h.id === id);
  const days = daysOf(done, id);

  const stats = useMemo(() => {
    if (!habit) return null;
    return { streak: streaks(habit, days, today), rate: completionRate(habit, days, today) };
  }, [habit, days, today]);

  if (!loaded) return <div className="view" />;
  if (!habit || !stats) {
    return (
      <div className="view">
        <section className="notice glass">
          <p>Dieses Habit gibt es nicht (mehr).</p>
          <button type="button" className="btn btn--glass btn--sm" onClick={() => goBack('/habits')}>
            <ArrowLeft size={16} /> Zurück
          </button>
        </section>
      </div>
    );
  }

  const unit = stats.streak.unit === 'weeks' ? (n: number) => (n === 1 ? 'Woche' : 'Wochen') : (n: number) => (n === 1 ? 'Tag' : 'Tage');
  const weekly = habit.schedule.kind === 'weekly' ? { count: weekCount(days, today), target: habit.schedule.times } : null;

  const toggle = (day: DayKey, value: boolean) => {
    tap(value ? 10 : 4);
    setDone(habit.id, day, value);
    if (day !== today) toast({ text: `${relativeDay(day, today)}: ${value ? 'erledigt' : 'zurückgesetzt'}.`, durationMs: 1800 });
  };

  return (
    <div className={`view hc-${habit.color}`}>
      <div className="topbar">
        <button type="button" className="icon-btn icon-btn--glass" onClick={() => goBack('/verlauf')} aria-label="Zurück">
          <ArrowLeft size={20} />
        </button>
        <button type="button" className="btn btn--glass btn--sm" onClick={() => editor.openEdit(habit)}>
          <Pencil size={16} /> Bearbeiten
        </button>
      </div>

      <header className="hero">
        <EmojiTile emoji={habit.emoji} sticker={habit.sticker} color={habit.color} size="lg" />
        <div className="hero__text">
          <h1 className="hero__title">{habit.name}</h1>
          <p className="hero__meta">
            {scheduleLabel(habit.schedule)}
            {weekly && ` · diese Woche ${Math.min(weekly.count, weekly.target)} von ${weekly.target}`}
          </p>
        </div>
      </header>

      {habit.archived && (
        <section className="notice glass notice--row">
          <p>Pausiert – erscheint nicht unter „Heute“.</p>
          <button
            type="button"
            className="btn btn--glass btn--sm"
            onClick={() => updateHabit(habit.id, { archived: false }).catch((err) => toast({ text: errorMessage(err), kind: 'error' }))}
          >
            <Play size={16} /> Fortsetzen
          </button>
        </section>
      )}

      <div className="stats">
        <div className="stat glass">
          <span className="stat__label">
            <Flame size={15} aria-hidden="true" className="stat__flame" /> Aktuelle Serie
          </span>
          <span className="stat__value">
            {stats.streak.current}
            <span className="stat__unit">{unit(stats.streak.current)}</span>
          </span>
        </div>
        <div className="stat glass">
          <span className="stat__label">
            <Trophy size={15} aria-hidden="true" /> Beste Serie
          </span>
          <span className="stat__value">
            {stats.streak.best}
            <span className="stat__unit">{unit(stats.streak.best)}</span>
          </span>
        </div>
        <div className="stat glass">
          <span className="stat__label">
            <TrendingUp size={15} aria-hidden="true" /> Quote 30 Tage
          </span>
          <span className="stat__value">{percent(stats.rate.ratio)}</span>
        </div>
        <div className="stat glass">
          <span className="stat__label">
            <CalendarCheck size={15} aria-hidden="true" /> Insgesamt
          </span>
          <span className="stat__value">
            {days.size}
            <span className="stat__unit">× erledigt</span>
          </span>
        </div>
      </div>

      <MonthCalendar habit={habit} days={days} today={today} month={month} onMonth={setMonth} onToggle={toggle} />
      <p className="hint">Tippe auf einen Tag, um ihn nachträglich abzuhaken oder zurückzusetzen.</p>
    </div>
  );
}
